import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import config from '../Config';
import { authFetch } from '../functions/authFetch';
import { HandleResponse } from '../functions/HandleResponse';
import './pages.css';

// Keys become environment variable names, so case is free but a leading digit
// would make the variable unreachable from most shells.
const ENV_KEY_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;
const ENV_KEY_HINT = 'Use letters, digits and underscores, starting with a letter or underscore.';

const emptyRow = () => ({ key: '', value: '' });

export default function SecretManagement() {
  const navigate = useNavigate();

  // list state
  const [secrets, setSecrets] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);

  // detail panel — key names only; the API never returns values
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [keys, setKeys] = useState([]);
  const [detailError, setDetailError] = useState(null);

  // replace-values editor for the selected secret
  const [editing, setEditing] = useState(false);
  const [editMode, setEditMode] = useState('kv'); // 'kv' | 'file'
  const [editRows, setEditRows] = useState([emptyRow()]);
  const [editFile, setEditFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);

  // delete
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // unsaved-changes guard: name of secret user wants to switch to while create is open
  const [discardTarget, setDiscardTarget] = useState(null);

  // create panel
  const [showCreate, setShowCreate] = useState(false);
  const [createMode, setCreateMode] = useState('kv'); // 'kv' | 'file'
  const [createName, setCreateName] = useState('');
  const [createRows, setCreateRows] = useState([emptyRow()]);
  const [createFile, setCreateFile] = useState(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [createMsg, setCreateMsg] = useState(null);

  const base = `${config.RUNNER_API}/v1/secret`;

  // ── list ────────────────────────────────────────────────────────────────────
  const fetchList = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      const res = await authFetch(`${base}/list`, { method: 'GET' });
      await HandleResponse(res, navigate);
      if (res.ok) {
        const data = await res.json();
        setSecrets(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      setListError('Failed to load Secrets.');
    } finally {
      setListLoading(false);
    }
  }, [base, navigate]);

  useEffect(() => { fetchList(); }, [fetchList]);

  // ── create form has user input? ──────────────────────────────────────────────
  const createHasInput = () =>
    createName.trim() !== '' ||
    createFile !== null ||
    createRows.some(r => r.key.trim() !== '' || r.value.trim() !== '');

  // ── view/select ──────────────────────────────────────────────────────────────
  const doSelectSecret = useCallback(async (name) => {
    setShowCreate(false);
    setCreateError(null);
    setCreateMsg(null);
    setSelected(name);
    setDetailLoading(true);
    setDetailError(null);
    setSaveMsg(null);
    setEditing(false);
    setEditFile(null);
    setKeys([]);
    try {
      const res = await authFetch(`${base}/keys?name=${encodeURIComponent(name)}`, { method: 'GET' });
      await HandleResponse(res, navigate);
      if (res.ok) {
        const body = await res.json();
        setKeys(Array.isArray(body.keys) ? body.keys : []);
      }
    } catch (e) {
      setDetailError('Failed to load Secret details.');
    } finally {
      setDetailLoading(false);
    }
  }, [base, navigate]);

  const selectSecret = useCallback((name) => {
    if (showCreate && createHasInput()) {
      setDiscardTarget(name);
      return;
    }
    doSelectSecret(name);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCreate, createName, createFile, createRows, doSelectSecret]);

  // ── replace values ───────────────────────────────────────────────────────────
  const startEditing = () => {
    // Existing keys are pre-filled so the shape is easy to reproduce, but every
    // value has to be re-entered — we cannot read the current ones back.
    setEditRows(keys.length ? keys.map(k => ({ key: k, value: '' })) : [emptyRow()]);
    setEditMode('kv');
    setEditFile(null);
    setDetailError(null);
    setSaveMsg(null);
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setDetailError(null);
    setSaveMsg(null);
    try {
      let res;
      if (editMode === 'file') {
        if (!editFile) { setDetailError('Please select a .env file.'); setSaving(false); return; }
        const form = new FormData();
        form.append('env_file', editFile);
        res = await authFetch(`${base}/create-from-file?name=${encodeURIComponent(selected)}`, {
          method: 'POST',
          body: form,
        });
      } else {
        const dataObj = {};
        for (const { key, value } of editRows) {
          const k = key.trim();
          if (!k) continue;
          if (!ENV_KEY_RE.test(k)) {
            setDetailError(`Key "${k}" is not a valid environment variable name. ${ENV_KEY_HINT}`);
            setSaving(false);
            return;
          }
          if (value === '') {
            setDetailError(`Key "${k}" needs a value. Values cannot be read back, so every key must be re-entered.`);
            setSaving(false);
            return;
          }
          dataObj[k] = value;
        }
        if (Object.keys(dataObj).length === 0) {
          setDetailError('Add at least one key/value pair.');
          setSaving(false);
          return;
        }
        res = await authFetch(`${base}/create?name=${encodeURIComponent(selected)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(dataObj),
        });
      }

      await HandleResponse(res, navigate);
      if (res.ok) {
        const body = await res.json().catch(() => ({}));
        setKeys(Array.isArray(body.keys) ? body.keys : keys);
        setEditing(false);
        setEditFile(null);
        setSaveMsg('Secret updated. Running workloads pick it up on their next run or redeploy.');
        fetchList();
      } else {
        const err = await res.json().catch(() => ({}));
        setDetailError(err.detail || 'Save failed.');
      }
    } catch (e) {
      setDetailError('Save failed.');
    } finally {
      setSaving(false);
    }
  };

  // ── delete ───────────────────────────────────────────────────────────────────
  const handleDelete = async (name) => {
    setDeleting(true);
    try {
      const res = await authFetch(`${base}/delete?name=${encodeURIComponent(name)}`, { method: 'DELETE' });
      if (res.status === 409) {
        const data = await res.json().catch(() => ({}));
        setListError(data.detail || `Secret "${name}" is in use by a deployment and cannot be deleted.`);
        return;
      }
      await HandleResponse(res, navigate);
      if (res.ok) {
        if (selected === name) setSelected(null);
        setListError(null);
        fetchList();
      }
    } catch (e) {
      // swallow — list refresh handles visual state
    } finally {
      setDeleting(false);
      setDeleteConfirm(null);
    }
  };

  // ── create ───────────────────────────────────────────────────────────────────
  const handleCreate = async () => {
    setCreating(true);
    setCreateError(null);
    setCreateMsg(null);
    const name = createName.trim();
    if (!name) { setCreateError('Name is required.'); setCreating(false); return; }

    try {
      let res;
      if (createMode === 'file') {
        if (!createFile) { setCreateError('Please select a .env file.'); setCreating(false); return; }
        const form = new FormData();
        form.append('env_file', createFile);
        res = await authFetch(`${base}/create-from-file?name=${encodeURIComponent(name)}`, {
          method: 'POST',
          body: form,
        });
      } else {
        const dataObj = {};
        for (const { key, value } of createRows) {
          const k = key.trim();
          if (!k) continue;
          if (!ENV_KEY_RE.test(k)) {
            setCreateError(`Key "${k}" is not a valid environment variable name. ${ENV_KEY_HINT}`);
            setCreating(false);
            return;
          }
          if (value === '') {
            setCreateError(`Key "${k}" needs a value.`);
            setCreating(false);
            return;
          }
          dataObj[k] = value;
        }
        if (Object.keys(dataObj).length === 0) {
          setCreateError('Add at least one key/value pair.');
          setCreating(false);
          return;
        }
        res = await authFetch(`${base}/create?name=${encodeURIComponent(name)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(dataObj),
        });
      }

      await HandleResponse(res, navigate);
      if (res.ok) {
        setCreateMsg(`Secret "${name}" created.`);
        setCreateName('');
        setCreateRows([emptyRow()]);
        setCreateFile(null);
        fetchList();
      } else {
        const err = await res.json().catch(() => ({}));
        setCreateError(err.detail || 'Create failed.');
      }
    } catch (e) {
      setCreateError('Create failed.');
    } finally {
      setCreating(false);
    }
  };

  // ── render ───────────────────────────────────────────────────────────────────
  return (
    <div className="cm-page">
      <h2 className="cm-title">
        Secrets
        <span className="cj-dedicated-badge">write-only</span>
      </h2>
      <p className="cm-subtitle">
        Store API keys, passwords and tokens, and inject them into jobs, cron jobs and
        deployments as environment variables.
      </p>

      <div className="cm-layout">
        {/* ── LEFT: list ── */}
        <div className="cm-sidebar">
          <div className="cm-sidebar-header">
            <span>Secrets</span>
            <button
              className="cm-btn cm-btn-primary cm-btn-sm"
              onClick={() => {
                setShowCreate(c => !c);
                setCreateError(null);
                setCreateMsg(null);
                setCreateName('');
                setCreateRows([emptyRow()]);
                setCreateFile(null);
                if (!showCreate) setSelected(null);
              }}
            >
              {showCreate ? 'Cancel' : '+ New'}
            </button>
          </div>

          {listLoading && <p className="cm-hint">Loading…</p>}
          {listError && <p className="cm-error">{listError}</p>}
          {!listLoading && secrets.length === 0 && <p className="cm-hint">No Secrets found.</p>}

          <ul className="cm-list">
            {secrets.map(name => (
              <li key={name} className={`cm-list-item ${selected === name ? 'active' : ''}`}>
                <span className="cm-list-name" onClick={() => selectSecret(name)} title={name}>
                  {name}
                </span>
                <button
                  className="cm-btn cm-btn-danger cm-btn-xs"
                  onClick={(e) => { e.stopPropagation(); setDeleteConfirm(name); }}
                >
                  Delete
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* ── RIGHT: detail / create ── */}
        <div className="cm-detail">
          {showCreate ? (
            <div className="cm-create-panel">
              <h3 className="cm-panel-title">Create Secret</h3>

              <label className="cm-label">Name</label>
              <div className="cm-input-wrap">
                <input
                  className="cm-input"
                  value={createName}
                  onChange={e => setCreateName(e.target.value)}
                  placeholder="my-app-credentials"
                />
              </div>

              <div className="cm-mode-tabs">
                <button
                  className={`cm-mode-tab ${createMode === 'kv' ? 'active' : ''}`}
                  onClick={() => setCreateMode('kv')}
                >Key/Value</button>
                <button
                  className={`cm-mode-tab ${createMode === 'file' ? 'active' : ''}`}
                  onClick={() => setCreateMode('file')}
                >.env File</button>
              </div>

              {createMode === 'kv' ? (
                <div className="cm-kv-editor">
                  {createRows.map((row, i) => (
                    <div className="cm-kv-row" key={i}>
                      <input
                        className="cm-input cm-kv-key"
                        placeholder="KEY"
                        value={row.key}
                        onChange={e => setCreateRows(r => r.map((x, idx) => idx === i ? { ...x, key: e.target.value } : x))}
                      />
                      <span className="cm-kv-eq">=</span>
                      <input
                        className="cm-input cm-kv-val"
                        type="password"
                        autoComplete="new-password"
                        placeholder="value"
                        value={row.value}
                        onChange={e => setCreateRows(r => r.map((x, idx) => idx === i ? { ...x, value: e.target.value } : x))}
                      />
                      {createRows.length > 1 && (
                        <button className="cm-btn cm-btn-ghost cm-btn-xs" onClick={() => setCreateRows(r => r.filter((_, idx) => idx !== i))}>✕</button>
                      )}
                    </div>
                  ))}
                  <div className="cm-kv-footer">
                    <button className="cm-btn cm-btn-ghost cm-btn-sm" onClick={() => setCreateRows(r => [...r, emptyRow()])}>+ Add row</button>
                    {createError && <p className="cm-error">{createError}</p>}
                    {createMsg && <p className="cm-success">{createMsg}</p>}
                    <button className="cm-btn cm-btn-primary cm-btn-sm" onClick={handleCreate} disabled={creating}>
                      {creating ? 'Creating…' : 'Create Secret'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="cm-file-upload">
                  <div className="mcr-file-upload">
                    <input
                      id="secret-env-file-input"
                      type="file"
                      accept=".env,text/plain"
                      className="mcr-file-input-hidden"
                      onChange={e => setCreateFile(e.target.files[0] || null)}
                    />
                    <button
                      type="button"
                      className="mcr-select-file-btn"
                      onClick={() => document.getElementById('secret-env-file-input')?.click()}
                    >
                      Choose file
                    </button>
                    {createFile && <span className="mcr-file-name">{createFile.name}</span>}
                  </div>
                  <p className="cm-hint">Upload a <code>.env</code> file with <code>KEY=value</code> lines.</p>
                  {createError && <p className="cm-error">{createError}</p>}
                  {createMsg && <p className="cm-success">{createMsg}</p>}
                  <button className="cm-btn cm-btn-primary cm-btn-sm" onClick={handleCreate} disabled={creating}>
                    {creating ? 'Creating…' : 'Create Secret'}
                  </button>
                </div>
              )}
            </div>
          ) : selected ? (
            <div className="cm-edit-panel">
              <div className="cm-edit-header">
                <h3 className="cm-panel-title">{selected}</h3>
                <button className="cm-btn cm-btn-ghost cm-btn-sm" onClick={() => setSelected(null)}>✕ Close</button>
              </div>

              {detailLoading && <p className="cm-hint">Loading…</p>}
              {detailError && <p className="cm-error">{detailError}</p>}

              {!detailLoading && !editing && (
                <>
                  <div className="cm-kv-editor">
                    <div className="cm-kv-row cm-kv-header">
                      <span className="cm-kv-key">Key</span>
                      <span className="cm-kv-eq" />
                      <span className="cm-kv-val">Value</span>
                    </div>
                    {keys.length === 0 && <p className="cm-hint">This Secret has no keys.</p>}
                    {keys.map(k => (
                      <div className="cm-kv-row" key={k}>
                        <span className="cm-kv-key secret-key-name">{k}</span>
                        <span className="cm-kv-eq">=</span>
                        <span className="cm-kv-val secret-masked">••••••••••••</span>
                      </div>
                    ))}
                  </div>

                  {saveMsg && <p className="cm-success">{saveMsg}</p>}

                  <p className="cm-hint">
                    Values are write-only — they are never sent back to the dashboard. Replacing
                    the Secret overwrites every key, so re-enter all of them.
                  </p>

                  <div className="cm-edit-actions">
                    <button className="cm-btn cm-btn-primary" onClick={startEditing}>
                      Replace Values
                    </button>
                  </div>
                </>
              )}

              {!detailLoading && editing && (
                <>
                  <div className="cm-mode-tabs">
                    <button
                      className={`cm-mode-tab ${editMode === 'kv' ? 'active' : ''}`}
                      onClick={() => setEditMode('kv')}
                    >Key/Value</button>
                    <button
                      className={`cm-mode-tab ${editMode === 'file' ? 'active' : ''}`}
                      onClick={() => setEditMode('file')}
                    >.env File</button>
                  </div>

                  {editMode === 'kv' ? (
                    <div className="cm-kv-editor">
                      {editRows.map((row, i) => (
                        <div className="cm-kv-row" key={i}>
                          <input
                            className="cm-input cm-kv-key"
                            placeholder="KEY"
                            value={row.key}
                            onChange={e => setEditRows(r => r.map((x, idx) => idx === i ? { ...x, key: e.target.value } : x))}
                          />
                          <span className="cm-kv-eq">=</span>
                          <input
                            className="cm-input cm-kv-val"
                            type="password"
                            autoComplete="new-password"
                            placeholder="new value"
                            value={row.value}
                            onChange={e => setEditRows(r => r.map((x, idx) => idx === i ? { ...x, value: e.target.value } : x))}
                          />
                          <button className="cm-btn cm-btn-ghost cm-btn-xs" onClick={() => setEditRows(r => r.filter((_, idx) => idx !== i))}>✕</button>
                        </div>
                      ))}
                      <button className="cm-btn cm-btn-ghost cm-btn-sm" onClick={() => setEditRows(r => [...r, emptyRow()])}>+ Add row</button>
                    </div>
                  ) : (
                    <div className="cm-file-upload">
                      <div className="mcr-file-upload">
                        <input
                          id="secret-env-file-replace"
                          type="file"
                          accept=".env,text/plain"
                          className="mcr-file-input-hidden"
                          onChange={e => setEditFile(e.target.files[0] || null)}
                        />
                        <button
                          type="button"
                          className="mcr-select-file-btn"
                          onClick={() => document.getElementById('secret-env-file-replace')?.click()}
                        >
                          Choose file
                        </button>
                        {editFile && <span className="mcr-file-name">{editFile.name}</span>}
                      </div>
                      <p className="cm-hint">The uploaded file replaces the whole Secret.</p>
                    </div>
                  )}

                  <div className="cm-edit-actions">
                    <button className="cm-btn cm-btn-ghost" onClick={() => { setEditing(false); setDetailError(null); }}>
                      Cancel
                    </button>
                    <button className="cm-btn cm-btn-primary" onClick={handleSave} disabled={saving}>
                      {saving ? 'Saving…' : 'Save Secret'}
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="cm-empty-state">
              <p>Select a Secret from the list to see its keys,<br />or click <strong>+ New</strong> to create one.</p>
            </div>
          )}
        </div>

        {/* ── GUIDE: instruction panel ── */}
        <div className="page-inst-panel">
          <h4>Secret vs ConfigMap</h4>
          <p>
            Both inject environment variables into your container. A Secret is for values
            you should not be able to read back — API keys, database passwords, tokens.
          </p>
          <hr className="inst-divider" />
          <h4>How it works</h4>
          <div className="inst-field">
            <span className="inst-field-name">Write-only</span>
            The dashboard and API return key names, never values. Nobody — including
            support — can read a value back once saved.
          </div>
          <div className="inst-field">
            <span className="inst-field-name">Encrypted</span>
            Stored encrypted at rest and handed to your container only at run time.
          </div>
          <div className="inst-field">
            <span className="inst-field-name">.env upload</span>
            Upload a <code>KEY=value</code> file to set every key at once.
          </div>
          <hr className="inst-divider" />
          <p>
            Attach a Secret when you create a job or cron job, or on a deployment&rsquo;s
            configuration.
          </p>
          <p>
            Changes take effect on the next job or cron run, and on the next
            <strong> Redeploy</strong> for a running deployment.
          </p>
          <p>If a Secret and a ConfigMap define the same key, the Secret wins.</p>
        </div>
      </div>

      {/* ── unsaved changes modal ── */}
      {discardTarget && (
        <div className="popup" onClick={() => setDiscardTarget(null)}>
          <div className="popup-inner" onClick={e => e.stopPropagation()}>
            <h2>Unsaved Changes</h2>
            <div>You have unsaved changes in the create form. Discard them and open <strong>{discardTarget}</strong>?</div>
            <div className="cm-popup-actions">
              <button className="cm-btn cm-btn-ghost" onClick={() => setDiscardTarget(null)}>Continue Editing</button>
              <button
                className="cm-btn cm-btn-danger"
                onClick={() => {
                  const target = discardTarget;
                  setDiscardTarget(null);
                  setCreateName('');
                  setCreateRows([emptyRow()]);
                  setCreateFile(null);
                  doSelectSecret(target);
                }}
              >
                Discard &amp; Open
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── delete confirm modal ── */}
      {deleteConfirm && (
        <div className="cm-modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="cm-modal" onClick={e => e.stopPropagation()}>
            <h4 className="cm-modal-title">Delete Secret</h4>
            <p className="cm-modal-body">
              Are you sure you want to delete <strong>{deleteConfirm}</strong>? The values cannot
              be recovered.
            </p>
            <div className="cm-modal-buttons">
              <button className="cm-btn cm-btn-ghost" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="dm-btn-delete" onClick={() => handleDelete(deleteConfirm)} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
